import { useMemo, useState } from "react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Download, Share2 } from "lucide-react";

/**
 * MobileAppDownloadButton
 * Mostra un pulsante contestuale al dispositivo:
 *  - **Android** → "Scarica App Android" (apre warning + download /api/download/android-app)
 *  - **iOS**     → "Crea collegamento sulla Home" (apre istruzioni Safari → Condividi → Aggiungi)
 *  - **Desktop** → nascosto (ritorna null)
 *
 * Props:
 *  - `variant` = "sidebar" | "inline"   (styling diverso)
 *  - `testIdPrefix` (per data-testid univoci se riusato in più punti)
 */
export default function MobileAppDownloadButton({ variant = "inline", testIdPrefix = "mobile-app" }) {
  const [open, setOpen] = useState(false);

  const platform = useMemo(() => {
    if (typeof navigator === "undefined") return "desktop";
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/.test(ua)) return "ios";
    return "desktop";
  }, []);

  if (platform === "desktop") return null;

  const isAndroid = platform === "android";
  const title = isAndroid ? "Scarica App Android" : "Crea collegamento sulla Home";
  const Icon = isAndroid ? Smartphone : Share2;

  const triggerDownload = () => {
    // Download tramite redirect: /api/download/android-app è pubblico
    window.location.href = `${API}/download/android-app`;
    setOpen(false);
  };

  const sidebarClass = `w-full flex items-center justify-center gap-2 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-200 text-sm py-2 transition-colors`;
  const inlineClass = `inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={variant === "sidebar" ? sidebarClass : inlineClass}
        data-testid={`${testIdPrefix}-open`}
      >
        <Icon className="h-4 w-4" />
        <span>{title}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <Icon className="h-5 w-5" />
              {title}
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-300">
              {isAndroid ? (
                <>
                  L'app <strong>non è sul Play Store</strong>: scaricandola da qui, Android mostrerà un avviso di sicurezza. Per installarla devi accettarlo (<em>consenti l'installazione da questa fonte</em>).
                  <br /><br />
                  È una soluzione sperimentale e <strong>non si aggiorna da sola</strong>: per avere una versione nuova devi scaricarla di nuovo da qui.
                </>
              ) : (
                <>
                  Su iPhone/iPad non puoi installare un APK Android. Puoi però aggiungere un <strong>collegamento alla schermata Home</strong> che apre il portale a schermo intero come fosse un'app:
                  <ol className="list-decimal pl-5 mt-3 space-y-1 text-sm">
                    <li>Apri questa pagina in <strong>Safari</strong>.</li>
                    <li>Tocca l'icona <Share2 className="inline h-3.5 w-3.5 -mt-0.5" /> <strong>Condividi</strong> in basso.</li>
                    <li>Scorri e scegli <strong>"Aggiungi alla schermata Home"</strong>.</li>
                    <li>Conferma: l'icona del portale compare come un'app normale.</li>
                  </ol>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid={`${testIdPrefix}-cancel`}
            >
              Annulla
            </Button>
            {isAndroid ? (
              <Button
                onClick={triggerDownload}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`${testIdPrefix}-confirm`}
              >
                <Download className="h-4 w-4 mr-1" /> Ho capito, scarica
              </Button>
            ) : (
              <Button
                onClick={() => setOpen(false)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`${testIdPrefix}-confirm`}
              >
                Ho capito
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
